import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import StatusMessage from "../../../components/common/StatusMessage";
import { fetchLessonPlacements } from "../../../utils/lessonV2Api";
import {
  fetchScheduleOverrides,
  upsertScheduleOverrides,
  deleteScheduleOverride,
  upsertAllClosed,
  deleteAllClosed,
} from "../../../utils/scheduleApi";
import { toPng } from "html-to-image";
import JSZip from "jszip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImages, faDownload } from "@fortawesome/free-solid-svg-icons";
import ScheduleImageTemplate from "./ScheduleImageTemplate";
import { buildScheduleEntries } from "./scheduleImageData";

// 設計: membership_kyuko_spec_v5.md 2 章（5 種＋全クラス休講）
const STATUS = {
  open: { label: "営業", color: "#76bbff" },
  closed: { label: "休講", color: "#ff6b6b" },
  substitute: { label: "代講", color: "#b8efe2" },
  reschedule: { label: "振替", color: "#c9ff82" },
  timeChange: { label: "時間変更", color: "#ecff82" },
};

// 一部○○バッジのオレンジ（v5 2.2.2）／全クラス休講バッジ
const PARTIAL_BADGE_COLOR = "#ffa940";
const ALL_CLOSED_BADGE_COLOR = "#888";

// 一部○○判定時の優先度（v5 2.2.1 #3）: closed > reschedule > substitute > timeChange
const STATUS_PRIORITY = {
  closed: 4,
  reschedule: 3,
  substitute: 2,
  timeChange: 1,
  open: 0,
};

const WEEKDAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

// 備考（Schedule.note）の入力上限。画像（1080px 幅）では全角 49 文字で 1 行、
// 60 文字なら 2 行に折り返して収まる。これ以上は縦の余白を食うため上限にしている。
const NOTE_MAX = 60;
const WEEKDAY_KANJI = ["日", "月", "火", "水", "木", "金", "土"];

// JSのgetDay(0=日)とAPIの曜日キーをマップ
const WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

// 校舎カラー（APIには無いのでフロント側で割当）
const STUDIO_PALETTE = ["#ffc4d6", "#b8efe2", "#ecff82", "#c9ff82", "#ffb0b0"];

// プレビューモーダルでの 1080×1920 縮小率（324×576 で表示）
const PREVIEW_SCALE = 0.3;

const BREADCRUMBS = [
  { title: "Home", path: "/" },
  { title: "Schedule", path: "/schedule" },
];

// "YYYY-MM-DD" 形式でゼロパディング（input[type="date"] の value と等値、API 連携でも使える）
const dateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// 校舎略名（"浜線校" → "浜線"）。長い校舎名は CSS の text-overflow に任せて切り詰めない。
const studioShortName = (name) => (name || "").replace(/校$/, "");

// 振替元日付の表示書式（v5 2.2.5）: "YYYY-MM-DD" → "M/D（曜日漢字）分の振替"
const formatRescheduleFrom = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  const parts = yyyymmdd.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return yyyymmdd;
  const [y, m, d] = parts;
  const dt = new Date(y, m - 1, d);
  // Date は不正な月日を繰り上げてしまうため、戻り値で一致確認（"2026-13-40" 等を弾く）
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== m - 1 ||
    dt.getDate() !== d
  ) {
    return yyyymmdd;
  }
  return `${m}/${d}（${WEEKDAY_KANJI[dt.getDay()]}）分の振替`;
};

// 振替先日付の表示書式（元セル注記用）: "YYYY-MM-DD" → "M/D（曜日漢字）へ振替"
// 振替は「元＝休講、先＝◯分の振替」の2箇所表示。元は closed で保存し、
// reschedule_from_date に“振替先”を格納する（列を増やさず1レコードで表現）。
const formatRescheduleTo = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  const parts = yyyymmdd.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return yyyymmdd;
  const [y, m, d] = parts;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
    return yyyymmdd;
  }
  return `${m}/${d}（${WEEKDAY_KANJI[dt.getDay()]}）へ振替`;
};

// 振替先で開催時刻が元クラスと変わっているか。
// 変わったときだけ new_start/new_end を保存・表示する（変更なしなら元の時刻のまま）。
const isRescheduleTimeChanged = (l) =>
  !!(
    l.newStart &&
    l.newEnd &&
    (l.newStart !== l.start || l.newEnd !== l.end)
  );

// "YYYY-MM-DD" → "M/D（曜日漢字）"（プレビュー文言用）
const formatDateLabel = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  const parts = yyyymmdd.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return yyyymmdd;
  const [y, m, d] = parts;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
    return yyyymmdd;
  }
  return `${m}/${d}（${WEEKDAY_KANJI[dt.getDay()]}）`;
};

// 振替先ラベル: 時刻変更なし "M/D（曜）へ振替" / ありなら "M/D（曜） HH:MM-HH:MMへ振替"
const formatRescheduleToLabel = (l) =>
  isRescheduleTimeChanged(l)
    ? `${formatDateLabel(l.rescheduleTo)} ${l.newStart}-${l.newEnd}へ振替`
    : formatRescheduleTo(l.rescheduleTo);

function buildMonthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

// 備考の入力欄は休講・時間変更のみ。代講等へ切替後に残った見えない備考は空として扱う
function effectiveNote(l) {
  return l.status === "closed" || l.status === "timeChange" ? l.note || "" : "";
}

// space枠（空のプレースホルダ）を除外して有効レッスンだけ返す
function isValidLesson(l) {
  return !!(
    (l.lesson_name && l.lesson_name.trim()) ||
    (l.start_time && l.start_time.trim()) ||
    (l.end_time && l.end_time.trim()) ||
    (l.instructor_name && l.instructor_name.trim())
  );
}

// HH:MM:SS → HH:MM
const trimTime = (t) => (t ? t.slice(0, 5) : "");

function SchedulePage() {
  // セッション中の "今日" は不変として扱う
  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  // 編集中のクラス単位状態: { [dateKey]: Lesson[] }
  const [editsByDate, setEditsByDate] = useState({});
  // 全クラス休講: 校舎別 or "all"（v5 7.4.2 と整合、studio=NULL = 全校）
  // 形： { [dateKey]: Array<number | "all"> }
  const [allClosedStudiosByDate, setAllClosedStudiosByDate] = useState({});
  // サーバから取得した原本（差分検出 / Reset の基準）
  const [initialEditsByDate, setInitialEditsByDate] = useState({});
  const [initialAllClosedStudiosByDate, setInitialAllClosedStudiosByDate] = useState({});
  // 全クラス休講のサーバ ID: { [dateKey]: { "all": id, "1": id, ... } }（削除 API 用）
  const [allClosedIdsByDate, setAllClosedIdsByDate] = useState({});
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success"); // success | error
  const [loadingText, setLoadingText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // SP のクラスリストで「タップで展開」する対象 lessonId（1 つだけ展開）
  const [expandedLessonId, setExpandedLessonId] = useState(null);
  // edit-header の「＋ 全クラス休講 ▾」ドロップダウンの開閉（PC モーダル / SP インライン共通）
  const [allClosedDropdownOpen, setAllClosedDropdownOpen] = useState(false);
  const [studios, setStudios] = useState([]);
  // 校舎ごとの曜日別レッスン: { [studioId]: { monday: [...], ... } }
  const [lessonsByStudio, setLessonsByStudio] = useState({});

  // 投稿用画像プレビュー（Phase 7-G）: プレビュー押下 → 全校舎の実 PNG を生成し、
  // モーダルに <img> で並べる。長押し（PC は右クリック）でそのまま端末に保存できる。
  // entries をオフスクリーン描画 → toPng で実画像化するため、中間 state を 2 つ持つ。
  // offscreen: { items:[{studioId,studioName,entries}], year, month } | null（描画トリガ用）
  const [offscreen, setOffscreen] = useState(null);
  // preview: { images:[{studioId,studioName,dataUrl}], year, month } | null（表示用）
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // 校舎ごとのオフスクリーン描画ノード参照 { [studioId]: HTMLElement }
  const imageNodeRefs = useRef({});
  // 校舎ごとの「1 枚に収まったか」{ [studioId]: true=収まらず下部が欠落 }
  // テンプレートのオートフィット縮小が下限(0.55)に張り付いた場合に true になる。
  const imageOverflowRef = useRef({});

  // 校舎リスト取得
  const fetchStudios = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(`${apiUrl}/api/get_page_access_content/`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return data.studios || [];
    } catch (error) {
      console.error("スタジオリストの取得に失敗しました", error);
      setMessage(`スタジオリストの取得に失敗しました: ${error.message}`);
      setTimeout(() => setMessage(""), 3000);
      return [];
    }
  }, []);

  // 1校舎分のレッスン取得
  // 設計: lesson_model_redesign_memo.md Phase 5.5 / Phase 4
  // 旧 /api/get_lessons/ は Phase 5 で廃止されたため、新 /api/get_lesson_placements/ を使う。
  // 旧 API のレスポンス（{ monday: [...], tuesday: [...] }）と互換になるよう、
  // ここで曜日別グルーピング + 旧キー名（lesson_name/target 等）へアダプトする。
  const fetchLessonsForStudio = useCallback(async (studioId) => {
    try {
      const data = await fetchLessonPlacements(studioId);
      const byDay = {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
      };
      (data.placements || []).forEach((p) => {
        // Space（空セル）は SchedulePage には不要なので除外
        if (p.space_flg) return;
        if (!byDay[p.day_of_week]) return;
        byDay[p.day_of_week].push({
          // 旧フォーマット互換キー
          lesson_id: p.id, // SchedulePage では一意 ID として使うだけ（placement.id をそのまま流用）
          lesson_name: p.content_name || "",
          start_time: p.start_time || "",
          end_time: p.end_time || "",
          instructor_name: p.instructor_name || "",
          target: p.content_target || "",
          // 将来の Schedule モデル参照用（休講機能で利用）
          placement_id: p.id,
        });
      });
      // 旧 API は曜日内 display_order でソートしていた。
      // SchedulePage 側で最終的に開始時刻順に並べ直すが、安定順序のためここでも揃える。
      Object.values(byDay).forEach((arr) =>
        arr.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
      );
      return byDay;
    } catch (error) {
      console.error(`レッスン取得失敗 (studio ${studioId})`, error);
      return {};
    }
  }, []);

  // 初回マウント時：校舎一覧 → 各校舎のレッスンを並列取得
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingText("レッスン情報を読み込み中");
      const studiosList = await fetchStudios();
      if (cancelled) return;
      // 校舎にカラー割当
      const studiosWithColor = studiosList.map((s, i) => ({
        ...s,
        color: STUDIO_PALETTE[i % STUDIO_PALETTE.length],
      }));
      setStudios(studiosWithColor);

      const lessonResults = await Promise.all(
        studiosWithColor.map((s) => fetchLessonsForStudio(s.id))
      );
      if (cancelled) return;
      const byStudio = {};
      studiosWithColor.forEach((s, i) => {
        byStudio[s.id] = lessonResults[i];
      });
      setLessonsByStudio(byStudio);
      setLoadingText("");
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchStudios, fetchLessonsForStudio]);

  // ある日付のレッスン一覧を全校舎まとめて生成（編集前の初期状態）
  const buildLessonsForDate = useCallback(
    (date) => {
      const weekdayKey = WEEKDAY_KEYS[date.getDay()];
      const list = [];
      studios.forEach((studio) => {
        const dayLessons = lessonsByStudio[studio.id]?.[weekdayKey] || [];
        dayLessons.filter(isValidLesson).forEach((l, idx) => {
          list.push({
            lessonId: `${dateKey(date)}-${studio.id}-${l.lesson_id || idx}`,
            // クラス同定キー（v5 7.1）。Schedule API への送信値として必須。
            placementId: l.placement_id || l.lesson_id,
            studioId: studio.id,
            name: l.lesson_name || "",
            target: l.target || "",
            start: trimTime(l.start_time),
            end: trimTime(l.end_time),
            instructor: l.instructor_name || "",
            status: "open",
            note: "",
            substituteInstructor: "",
            rescheduleFrom: "", // 旧方式（自日に「◯分の振替」）互換の保持用
            rescheduleTo: "", // 新方式の“振替先”日付（元は休講で保存）
            newStart: trimTime(l.start_time),
            newEnd: trimTime(l.end_time),
            // サーバ側の Schedule.id（既存登録の更新・削除に使う）。新規時は null。
            serverScheduleId: null,
          });
        });
      });
      // 開始時刻順に並べる
      list.sort((a, b) => (a.start || "").localeCompare(b.start || ""));
      return list;
    },
    [studios, lessonsByStudio]
  );

  // サーバレスポンス（v5 7.3）を内部状態に変換
  // overrides の各要素 → その日の baseList と merge して、編集中状態と同形式の Lesson[] を作る
  const mergeOverridesIntoBase = useCallback(
    (date, overrides) => {
      const base = buildLessonsForDate(date);
      const byPlacement = new Map(overrides.map((o) => [o.placement_id, o]));
      return base.map((lesson) => {
        const o = byPlacement.get(lesson.placementId);
        if (!o) return lesson;
        // 振替元（新方式）は closed + reschedule_from_date(=振替先) で保存されている。
        // 編集画面では「振替」タブに復元したいので status を reschedule に読み替える。
        const isMoveOut = o.status === "closed" && o.reschedule_from_date;
        return {
          ...lesson,
          status: isMoveOut ? "reschedule" : o.status,
          note: isMoveOut ? "" : o.note || "",
          substituteInstructor: o.substitute_instructor || "",
          rescheduleFrom:
            o.status === "reschedule" ? o.reschedule_from_date || "" : "",
          rescheduleTo: isMoveOut ? o.reschedule_from_date : "",
          newStart: o.new_start || lesson.newStart,
          newEnd: o.new_end || lesson.newEnd,
          serverScheduleId: o.schedule_id || null,
        };
      });
    },
    [buildLessonsForDate]
  );

  // 表示月の休講情報をサーバから取得して initial / 編集中の両方に反映
  // viewYear / viewMonth が変わった時、および studios / lessons の初期取得完了後に走る
  const loadScheduleOverrides = useCallback(
    async (year, month) => {
      if (studios.length === 0 || Object.keys(lessonsByStudio).length === 0) {
        return;
      }
      setLoadingText("休講情報を読み込み中");
      try {
        // API は 1-indexed の month を受ける（バックエンドは 1〜12 を期待）
        const data = await fetchScheduleOverrides(year, month + 1);
        const newEdits = {};
        const newInitialEdits = {};
        const newAllClosed = {};
        const newInitialAllClosed = {};
        const newAllClosedIds = {};

        Object.entries(data || {}).forEach(([dateStr, payload]) => {
          // payload: { overrides: [...], all_closed_studios?, all_closed_reason?, all_closed_ids? }
          const [y, m, d] = dateStr.split("-").map(Number);
          const date = new Date(y, m - 1, d);
          const overrides = payload.overrides || [];
          if (overrides.length > 0) {
            const merged = mergeOverridesIntoBase(date, overrides);
            // immutable な参照は使い回さず別オブジェクトでコピー
            newEdits[dateStr] = merged;
            newInitialEdits[dateStr] = merged.map((l) => ({ ...l }));
          }
          if (payload.all_closed_studios) {
            const list =
              payload.all_closed_studios === "all"
                ? ["all"]
                : Array.from(payload.all_closed_studios);
            newAllClosed[dateStr] = list;
            newInitialAllClosed[dateStr] = [...list];
          }
          if (payload.all_closed_ids) {
            newAllClosedIds[dateStr] = { ...payload.all_closed_ids };
          }
        });

        setEditsByDate(newEdits);
        setInitialEditsByDate(newInitialEdits);
        setAllClosedStudiosByDate(newAllClosed);
        setInitialAllClosedStudiosByDate(newInitialAllClosed);
        setAllClosedIdsByDate(newAllClosedIds);
      } catch (error) {
        console.error("休講情報の取得に失敗しました", error);
        setMessageType("error");
        setMessage(`休講情報の取得に失敗しました: ${error.message}`);
        setTimeout(() => setMessage(""), 4000);
      } finally {
        setLoadingText("");
      }
    },
    [studios, lessonsByStudio, mergeOverridesIntoBase]
  );

  // 月切替 / レッスン情報初期取得後にサーバから休講情報を再取得
  useEffect(() => {
    loadScheduleOverrides(viewYear, viewMonth);
  }, [viewYear, viewMonth, loadScheduleOverrides]);

  // 全校舎分の描画データ（entries）を構築。保存済み DB の状態を API から再取得する
  // （未保存の編集は含まない）。
  const buildAllStudioItems = useCallback(async () => {
    const apiMonth = viewMonth + 1; // API は 1-indexed
    // overrides は月単位で 1 回、placements は校舎ごとに並列取得
    const overridesRes = await fetchScheduleOverrides(viewYear, apiMonth);
    const placementsResList = await Promise.all(
      studios.map((s) => fetchLessonPlacements(s.id))
    );
    const items = studios.map((s, i) => ({
      studioId: s.id,
      studioName: s.name_ja,
      entries: buildScheduleEntries({
        year: viewYear,
        month: apiMonth,
        studioId: s.id,
        placements: placementsResList[i].placements || [],
        overridesResponse: overridesRes || {},
      }),
    }));
    return { items, year: viewYear, month: apiMonth };
  }, [studios, viewYear, viewMonth]);

  // 画像出力（Phase 7-G）: entries を取得しオフスクリーン描画をトリガ。
  // 実 PNG 化は下の useEffect が担当し、mode で「プレビュー表示」/「zip ダウンロード」を分岐。
  // mode: "preview"（スマホ=長押し保存 / PC=確認）| "download"（PC=zip 一括）
  const startImageExport = useCallback(
    async (mode) => {
      if (previewLoading || studios.length === 0) return;
      setPreviewLoading(true);
      setLoadingText("スケジュール画像を生成中");
      imageOverflowRef.current = {};
      try {
        const { items, year, month } = await buildAllStudioItems();
        setOffscreen({ items, year, month, mode });
      } catch (e) {
        setMessageType("error");
        setMessage(`画像データの取得に失敗しました: ${e.message}`);
        setTimeout(() => setMessage(""), 4000);
        setLoadingText("");
        setPreviewLoading(false);
      }
    },
    [previewLoading, studios, buildAllStudioItems]
  );

  const handlePreview = useCallback(
    () => startImageExport("preview"),
    [startImageExport]
  );
  const handleDownload = useCallback(
    () => startImageExport("download"),
    [startImageExport]
  );

  const closePreview = useCallback(() => setPreview(null), []);

  // PC: 全校舎の PNG を zip にまとめてダウンロード
  const downloadZip = useCallback((images, year, month) => {
    const zip = new JSZip();
    const mm = String(month).padStart(2, "0");
    images.forEach((img) => {
      // dataUrl ("data:image/png;base64,....") の base64 部分のみ格納
      zip.file(
        `schedule-${year}-${mm}-${img.studioName}.png`,
        img.dataUrl.split(",")[1],
        { base64: true }
      );
    });
    return zip.generateAsync({ type: "blob" }).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `schedule-${year}-${mm}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  }, []);

  // offscreen がセットされ描画が済んだら、全校舎を toPng → mode で分岐
  useEffect(() => {
    if (!offscreen) return;
    let cancelled = false;
    const run = async () => {
      const { items, year, month, mode } = offscreen;
      try {
        // ロゴ画像・フォントの描画完了を待つ
        await new Promise((r) => setTimeout(r, 450));
        if (cancelled) return;
        const images = [];
        // 校舎ごとに順次 toPng（同時多発でメモリを使い過ぎないよう直列）
        for (const it of items) {
          const node = imageNodeRefs.current[it.studioId];
          if (!node) continue;
          const dataUrl = await toPng(node, {
            width: 1080,
            height: 1920,
            pixelRatio: 1,
            cacheBust: true,
            backgroundColor: "#ffffff",
          });
          if (cancelled) return;
          images.push({
            studioId: it.studioId,
            studioName: it.studioName,
            dataUrl,
          });
        }
        if (cancelled) return;
        if (mode === "download") {
          await downloadZip(images, year, month);
        } else {
          setPreview({ images, year, month });
        }
        // 1 枚に収まらなかった校舎があれば知らせる（黙って日付が欠けるのを防ぐ）
        const overflowed = items
          .filter((it) => imageOverflowRef.current[it.studioId])
          .map((it) => it.studioName);
        if (overflowed.length > 0) {
          setMessageType("error");
          setMessage(
            `${overflowed.join("・")}は内容が多く、画像の下部に入りきらない日付があります。備考を短くするか、休講の件数をご確認ください`
          );
          setTimeout(() => setMessage(""), 8000);
        }
      } catch (e) {
        setMessageType("error");
        setMessage(`画像生成に失敗しました: ${e.message}`);
        setTimeout(() => setMessage(""), 5000);
      } finally {
        if (!cancelled) {
          setOffscreen(null);
          setLoadingText("");
          setPreviewLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [offscreen, downloadZip]);

  // SP の初回表示時：当日を自動選択（カレンダーのみで下が空 = UX が悪い）
  // PC ではモーダルが開いてしまうので発火しない
  const didInitialSPSelectRef = useRef(false);
  useEffect(() => {
    if (didInitialSPSelectRef.current) return;
    if (studios.length === 0 || Object.keys(lessonsByStudio).length === 0) return;
    if (!window.matchMedia("(max-width: 1024px)").matches) return;
    didInitialSPSelectRef.current = true;
    setSelectedDate(today);
  }, [studios, lessonsByStudio, today]);

  // selectedDate のレッスンが editsByDate に無ければ補完。
  // loadScheduleOverrides が月切替時に editsByDate を全置換するため、その後にも反応して埋める。
  useEffect(() => {
    if (!selectedDate) return;
    if (studios.length === 0 || Object.keys(lessonsByStudio).length === 0) return;
    const key = dateKey(selectedDate);
    if (editsByDate[key]) return;
    setEditsByDate((prev) => {
      if (prev[key]) return prev;
      return { ...prev, [key]: buildLessonsForDate(selectedDate) };
    });
  }, [selectedDate, editsByDate, studios, lessonsByStudio, buildLessonsForDate]);

  const matrix = useMemo(
    () => buildMonthMatrix(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  // 月切替は表示月のデータで編集状態を全置換するため、未保存の変更があれば確認する
  const confirmDiscardEdits = () =>
    !isDirty ||
    window.confirm("保存していない変更があります。破棄して月を移動しますか？");

  const handlePrevMonth = () => {
    if (!confirmDiscardEdits()) return;
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  };
  const handleNextMonth = () => {
    if (!confirmDiscardEdits()) return;
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  // SP では月切替後に「同じ day」を新月にスライドして lesson-list を継続表示する
  // （カレンダーだけが残って下が空、という UX を避ける）
  // useEffect で viewYear/viewMonth 変更を監視 → stale closure 回避 + 連打でも追従
  // PC ではモーダルが開かないよう selectedDate を変更しない
  const prevViewRef = useRef({ year: viewYear, month: viewMonth });
  useEffect(() => {
    const prev = prevViewRef.current;
    if (prev.year === viewYear && prev.month === viewMonth) return;
    prevViewRef.current = { year: viewYear, month: viewMonth };
    if (!window.matchMedia("(max-width: 1024px)").matches) return;
    const baseDay = (selectedDate || today).getDate();
    const lastDay = new Date(viewYear, viewMonth + 1, 0).getDate();
    const next = new Date(viewYear, viewMonth, Math.min(baseDay, lastDay));
    setSelectedDate(next);
    setAllClosedDropdownOpen(false);
    setExpandedLessonId(null);
    // editsByDate は別 useEffect で selectedDate を見て補完される
  }, [viewYear, viewMonth, selectedDate, today]);

  const handleSelectDate = (date) => {
    if (!date) return;
    const key = dateKey(date);
    setSelectedDate(date);
    // 日付を切り替えたらドロップダウンと展開中カードを閉じる
    setAllClosedDropdownOpen(false);
    setExpandedLessonId(null);
    if (!editsByDate[key]) {
      setEditsByDate((prev) => ({
        ...prev,
        [key]: buildLessonsForDate(date),
      }));
    }
  };

  // 振替先マップ（新方式）: editsByDate 内の振替元（reschedule + rescheduleTo）を
  // “振替先の日付キー”へ投影する。振替先セルは別レコードを持たず、ここから読取専用で派生表示する。
  // start/end は「振替先で実際に開催される時刻」（時間変更ありなら newStart/newEnd）。
  // 形: { [destKey]: Array<{ studioId, placementId, name, target, start, end,
  //       origStart, origEnd, timeChanged, instructor, originKey }> }
  const incomingReschedulesByDate = useMemo(() => {
    const map = {};
    Object.entries(editsByDate).forEach(([originKey, list]) => {
      (list || []).forEach((l) => {
        if (l.status === "reschedule" && l.rescheduleTo) {
          const destKey = l.rescheduleTo;
          // 振替先に同じ振替の旧方式レコードがあれば二重表示を避ける
          const legacyDup = (editsByDate[destKey] || []).some(
            (d) =>
              d.placementId === l.placementId &&
              d.status === "reschedule" &&
              !d.rescheduleTo &&
              d.rescheduleFrom === originKey
          );
          if (legacyDup) return;
          if (!map[destKey]) map[destKey] = [];
          const timeChanged = isRescheduleTimeChanged(l);
          map[destKey].push({
            studioId: l.studioId,
            placementId: l.placementId,
            name: l.name,
            target: l.target,
            // 振替先の実開催時刻。時間変更ありなら変更後、無ければ元クラスのまま
            start: timeChanged ? l.newStart : l.start,
            end: timeChanged ? l.newEnd : l.end,
            origStart: l.start,
            origEnd: l.end,
            timeChanged,
            instructor: l.instructor,
            originKey,
          });
        }
      });
    });
    return map;
  }, [editsByDate]);

  // 校舎単位の集計ステータス（v5 2.2.1）
  // 戻り値: Array<Summary>
  //   Summary = {
  //     kind: "all" | "studio",
  //     studioId?: number,
  //     studioName?: string,
  //     // 同校舎に混在する状態を優先度順で保持（最大 2 つまで表示用）
  //     statuses: Array<{ key: string, label: string, color: string }>,
  //     // "全クラス○○" or "一部○○" の判定（kind=="studio" のとき意味あり）
  //     isFullAll?: boolean,
  //     fullStudioClosed?: boolean,
  //     // 表示テキスト（"浜線 一部休講・振替" / "浜線 全休講" / "全クラス休講"）
  //     label: string,
  //   }
  // インバリアント: allClosedStudiosByDate[key] には "all" と個別 studioId が同時に
  // 含まれない（toggleAllStudiosClosed / toggleStudioClosed で保証）
  const getStudioSummaries = useCallback(
    (date) => {
      const key = dateKey(date);
      const closedList = allClosedStudiosByDate[key] || [];
      // 全校休講（"all"）が含まれていれば校舎別行は出さず 1 つだけ
      if (closedList.includes("all")) {
        return [{
          kind: "all",
          label: "全クラス休講",
          statuses: [{ key: "allClosed", label: "休講", color: ALL_CLOSED_BADGE_COLOR }],
        }];
      }

      const incoming = incomingReschedulesByDate[key] || [];
      const editedLessons = editsByDate[key];
      if (!editedLessons && closedList.length === 0 && incoming.length === 0)
        return [];

      const lessons = editedLessons || buildLessonsForDate(date);
      const summaries = [];

      // 振替元（reschedule + rescheduleTo）は元日では休講扱い（集計/バッジは closed）
      const effStatus = (l) =>
        l.status === "reschedule" && l.rescheduleTo ? "closed" : l.status;

      studios.forEach((studio) => {
        const studioLessons = lessons.filter((l) => l.studioId === studio.id);
        const studioIncoming = incoming.filter((i) => i.studioId === studio.id);
        if (studioLessons.length === 0 && studioIncoming.length === 0) return;

        const shortName = studioShortName(studio.name_ja);

        // 校舎単位の全クラス休講（ScheduleAllClosed studio=該当校舎）
        if (closedList.includes(studio.id)) {
          summaries.push({
            kind: "studio",
            studioId: studio.id,
            studioName: shortName,
            statuses: [{
              key: "closed",
              label: STATUS.closed.label,
              color: STATUS.closed.color,
            }],
            isFullAll: true,
            fullStudioClosed: true,
            prefix: "全",
            statusLabelText: STATUS.closed.label,
            label: `${shortName} 全休講`,
          });
          return;
        }

        const nonOpen = studioLessons.filter((l) => effStatus(l) !== "open");
        const hasIncoming = studioIncoming.length > 0;
        if (nonOpen.length === 0 && !hasIncoming) return;

        // 登場する状態を優先度順に重複なしで収集（振替先の投入は reschedule として加算）
        const priorityOf = (s) => STATUS_PRIORITY[s] ?? 0;
        const uniqueKinds = [];
        nonOpen.forEach((l) => {
          const s = effStatus(l);
          if (!uniqueKinds.includes(s)) uniqueKinds.push(s);
        });
        if (hasIncoming && !uniqueKinds.includes("reschedule"))
          uniqueKinds.push("reschedule");
        uniqueKinds.sort((a, b) => priorityOf(b) - priorityOf(a));

        // 表示用 statuses（最大 2 つまで。3 つ目以降は最初の 2 つの色だけで集約）
        const topStatuses = uniqueKinds.slice(0, 2).map((k) => ({
          key: k,
          label: STATUS[k].label,
          color: STATUS[k].color,
        }));

        // 「全○○」= 全クラスが同一非 open 状態。振替先の投入は“追加クラス”なので、
        // 投入のみ（元日の非 open なし）のときだけ全扱い、native と混在すれば一部。
        const topStatus = uniqueKinds[0];
        const onlyIncoming = nonOpen.length === 0 && hasIncoming;
        const isFullAll =
          uniqueKinds.length === 1 &&
          (onlyIncoming ||
            (!hasIncoming && nonOpen.length === studioLessons.length));

        // 振替先（受入のみ）は“追加クラス”なので全/一部を付けず「振替あり」と表記。
        // その日の自クラスが全部振替に変わったわけではないため（誤解防止）。
        const prefix = onlyIncoming ? "" : isFullAll ? "全" : "一部";
        const statusLabelText = onlyIncoming
          ? "振替あり"
          : topStatuses.map((s) => s.label).join("・");
        const label = `${shortName} ${prefix}${statusLabelText}`;

        summaries.push({
          kind: "studio",
          studioId: studio.id,
          studioName: shortName,
          statuses: topStatuses,
          isFullAll,
          topStatus,
          label,
          prefix,
          statusLabelText,
        });
      });

      return summaries;
    },
    [
      studios,
      editsByDate,
      allClosedStudiosByDate,
      buildLessonsForDate,
      incomingReschedulesByDate,
    ]
  );

  const selectedKey = selectedDate ? dateKey(selectedDate) : null;
  const lessons = selectedKey ? editsByDate[selectedKey] || [] : [];
  // この日に他日から振替で入ってくるクラス（読取専用。取消は振替元の日で行う）
  const selectedIncoming = selectedKey
    ? incomingReschedulesByDate[selectedKey] || []
    : [];
  const selectedAllClosed = selectedKey
    ? allClosedStudiosByDate[selectedKey] || []
    : [];
  const isAllStudiosClosed = selectedAllClosed.includes("all");

  // 選択日に有効レッスンを持つスタジオだけ、全クラス休講チェックの選択肢として表示
  const studiosWithLessonsForSelected = useMemo(() => {
    if (!selectedDate) return [];
    const weekdayKey = WEEKDAY_KEYS[selectedDate.getDay()];
    return studios.filter((studio) => {
      const dayLessons = lessonsByStudio[studio.id]?.[weekdayKey] || [];
      return dayLessons.some(isValidLesson);
    });
  }, [selectedDate, studios, lessonsByStudio]);

  const updateLesson = (idx, patch) => {
    setEditsByDate((prev) => ({
      ...prev,
      [selectedKey]: prev[selectedKey].map((l, i) =>
        i === idx ? { ...l, ...patch } : l
      ),
    }));
  };

  // 全スタジオ休講トグル：ON で "all" 単独、OFF で空配列
  const toggleAllStudiosClosed = () => {
    setAllClosedStudiosByDate((prev) => {
      const cur = prev[selectedKey] || [];
      const hasAll = cur.includes("all");
      const next = hasAll ? [] : ["all"];
      return { ...prev, [selectedKey]: next };
    });
  };

  // スタジオ別トグル："all" 状態から個別 OFF すると残りの校舎に展開（全部 ON でも "all" には戻さない）
  // 仕様：個別チェックの状態は「"all" もしくは個別 ID」で判定（isStudioChecked 参照）
  // 「全スタジオ」の範囲はその日レッスンを持つスタジオ集合に限定する
  const toggleStudioClosed = (studioId) => {
    setAllClosedStudiosByDate((prev) => {
      const cur = prev[selectedKey] || [];
      const allIds = studiosWithLessonsForSelected.map((s) => s.id);

      // "all" 状態から個別 OFF → その studio を除く他全 ID に展開
      if (cur.includes("all")) {
        return {
          ...prev,
          [selectedKey]: allIds.filter((id) => id !== studioId),
        };
      }

      // 通常の個別トグル。全校舎が揃っても "all" には正規化しない
      // （判定対象はその曜日にクラスがある校舎だけなので、振替で入る別校舎まで巻き込むため）
      const toggled = cur.includes(studioId)
        ? cur.filter((x) => x !== studioId)
        : [...cur, studioId];
      // 保存済みの "all" から外した校舎を戻しただけなら、元の "all" に戻す（未保存の差分を残さない）
      const backToInitialAll =
        (initialAllClosedStudiosByDate[selectedKey] || []).includes("all") &&
        allIds.length > 0 &&
        allIds.every((id) => toggled.includes(id));
      return { ...prev, [selectedKey]: backToInitialAll ? ["all"] : toggled };
    });
  };

  // 個別チェックの表示状態：「"all" もしくは個別チェック済」
  const isStudioChecked = (studioId) =>
    selectedAllClosed.includes("all") || selectedAllClosed.includes(studioId);

  // Reset: 表示中月の未保存変更をすべて破棄し、サーバ取得直後の状態（initial）に戻す
  // 「画面に遷移してきた時と同じ状態に戻す」体験を提供。編集モーダルも閉じる。
  // 他の管理画面と挙動を揃え、確認ダイアログは出さない。
  const handleReset = () => {
    if (!isDirty || isSubmitting) return;

    // initial をディープコピーして edit 側に戻す（mutation 防止）
    const restoredEdits = {};
    Object.entries(initialEditsByDate).forEach(([key, lessons]) => {
      restoredEdits[key] = lessons.map((l) => ({ ...l }));
    });
    const restoredAllClosed = {};
    Object.entries(initialAllClosedStudiosByDate).forEach(([key, arr]) => {
      restoredAllClosed[key] = [...arr];
    });

    setEditsByDate(restoredEdits);
    setAllClosedStudiosByDate(restoredAllClosed);
    setSelectedDate(null);
    setAllClosedDropdownOpen(false);
    setExpandedLessonId(null);
    setMessage("");
  };

  const handleCloseModal = () => {
    setSelectedDate(null);
    setAllClosedDropdownOpen(false);
  };

  // モーダル表示中は Esc キーで閉じる
  useEffect(() => {
    if (!selectedDate) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSelectedDate(null);
        setAllClosedDropdownOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedDate]);

  // プレビューモーダルも Esc で閉じる
  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPreview(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [preview]);

  // 「＋ 全クラス休講」ドロップダウンの外側クリックで閉じる
  useEffect(() => {
    if (!allClosedDropdownOpen) return undefined;
    // 編集 UI は SP インラインと PC モーダルの 2 箇所で同時にレンダーされるため、
    // ref だと後からマウントされたモーダル側だけを指してしまい、SP のタップが
    // 常に「外側」と判定されて mousedown の時点で閉じてしまう（change が発火しない）。
    // DOM ツリー上の位置で判定して両方の複製で正しく動くようにする。
    const onPointerDown = (e) => {
      if (!e.target.closest(".all-closed-dropdown-wrap")) {
        setAllClosedDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [allClosedDropdownOpen]);

  // SP: ドロップダウンを開いたら校舎リストの下端まで見えるようスクロールする
  // （画面が低い端末では開いた直後に下の校舎が隠れるため）
  useEffect(() => {
    if (!allClosedDropdownOpen) return;
    if (!window.matchMedia?.("(max-width: 1024px)").matches) return;
    const el = document.querySelector(".day-edit-inline .all-closed-dropdown");
    el?.scrollIntoView?.({ block: "end", behavior: "smooth" });
  }, [allClosedDropdownOpen]);

  // lesson-card の外側クリックで編集モード（展開状態）を閉じる
  useEffect(() => {
    if (!expandedLessonId) return undefined;
    const onPointerDown = (e) => {
      if (!e.target.closest(".lesson-card")) {
        setExpandedLessonId(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [expandedLessonId]);

  // 編集内容 ↔ 原本（initial）の差分を計算
  // - クラス単位: upsert / delete に分類
  // - 全クラス休講: upsert / delete に分類
  const submitPayload = useMemo(() => {
    const upsertOverrides = [];
    const deleteScheduleIds = [];
    const upsertAcItems = [];
    const deleteAcIds = [];

    // 比較対象の日付 = (現在の編集) ∪ (原本) のキー和集合
    const lessonKeys = new Set([
      ...Object.keys(editsByDate),
      ...Object.keys(initialEditsByDate),
    ]);
    lessonKeys.forEach((key) => {
      const current = editsByDate[key] || [];
      const initial = initialEditsByDate[key] || [];
      const initialByPlacement = new Map(initial.map((l) => [l.placementId, l]));
      const currentByPlacement = new Map(current.map((l) => [l.placementId, l]));

      // INSERT / UPDATE 判定
      currentByPlacement.forEach((cur, placementId) => {
        const init = initialByPlacement.get(placementId);
        const curIsActive = cur.status !== "open";
        const initIsActive = init && init.status !== "open";

        if (!curIsActive && !initIsActive) return; // 両方 open（無変化）
        if (!curIsActive && initIsActive) {
          // 元あった override が open に戻された → DELETE
          if (init.serverScheduleId) deleteScheduleIds.push(init.serverScheduleId);
          return;
        }
        // curIsActive: 内容が一致するなら何もしない、違えば upsert
        if (initIsActive) {
          const same =
            cur.status === init.status &&
            (cur.substituteInstructor || "") === (init.substituteInstructor || "") &&
            (cur.rescheduleFrom || "") === (init.rescheduleFrom || "") &&
            (cur.rescheduleTo || "") === (init.rescheduleTo || "") &&
            (cur.newStart || "") === (init.newStart || "") &&
            (cur.newEnd || "") === (init.newEnd || "") &&
            effectiveNote(cur) === effectiveNote(init);
          if (same) return;
        }
        // 振替（新方式）: 元日を closed で保存し reschedule_from_date に“振替先”を格納。
        // 振替先の表示は読取専用で派生（別レコードは作らない）。
        const isMoveOut = cur.status === "reschedule" && cur.rescheduleTo;
        const isLegacyReschedule = cur.status === "reschedule" && !cur.rescheduleTo;
        // 振替先の開催時刻が元と変わるときだけ new_start/new_end を持たせる（既存列を再利用）
        const moveOutTimeChanged = isMoveOut && isRescheduleTimeChanged(cur);
        upsertOverrides.push({
          placement_id: placementId,
          date: key,
          status: isMoveOut ? "closed" : cur.status,
          substitute_instructor:
            cur.status === "substitute" ? cur.substituteInstructor || "" : "",
          reschedule_from_date: isMoveOut
            ? cur.rescheduleTo
            : isLegacyReschedule
              ? cur.rescheduleFrom || null
              : null,
          new_start:
            cur.status === "timeChange" || moveOutTimeChanged
              ? cur.newStart || null
              : null,
          new_end:
            cur.status === "timeChange" || moveOutTimeChanged
              ? cur.newEnd || null
              : null,
          note: isMoveOut ? formatRescheduleToLabel(cur) : effectiveNote(cur),
        });
      });

      // 原本にあって現在に存在しない placement（buildLessonsForDate の結果が変わった等）
      initialByPlacement.forEach((init, placementId) => {
        if (currentByPlacement.has(placementId)) return;
        if (init.status !== "open" && init.serverScheduleId) {
          deleteScheduleIds.push(init.serverScheduleId);
        }
      });
    });

    // 全クラス休講の差分
    const acKeys = new Set([
      ...Object.keys(allClosedStudiosByDate),
      ...Object.keys(initialAllClosedStudiosByDate),
    ]);
    acKeys.forEach((key) => {
      const current = new Set(allClosedStudiosByDate[key] || []);
      const initial = new Set(initialAllClosedStudiosByDate[key] || []);
      // 追加 = current - initial
      current.forEach((studio) => {
        if (initial.has(studio)) return;
        upsertAcItems.push({ date: key, studio_id: studio, reason: "" });
      });
      // 削除 = initial - current
      initial.forEach((studio) => {
        if (current.has(studio)) return;
        const idMap = allClosedIdsByDate[key];
        if (!idMap) return;
        const acId = idMap[String(studio)];
        // replaced: 同じ日に別の全クラス休講を追加する（全校⇔校舎別の切替）＝置き換え側。
        // 外すだけのものは false（先に送り、同じ保存の振替が古い休講で弾かれないようにする）
        const added = [...current].some((x) => !initial.has(x));
        if (acId) deleteAcIds.push({ id: acId, replaced: added });
      });
    });

    return { upsertOverrides, deleteScheduleIds, upsertAcItems, deleteAcIds };
  }, [
    editsByDate,
    initialEditsByDate,
    allClosedStudiosByDate,
    initialAllClosedStudiosByDate,
    allClosedIdsByDate,
  ]);

  const isDirty =
    submitPayload.upsertOverrides.length > 0 ||
    submitPayload.deleteScheduleIds.length > 0 ||
    submitPayload.upsertAcItems.length > 0 ||
    submitPayload.deleteAcIds.length > 0;

  const handleSubmit = async () => {
    if (!isDirty || isSubmitting) return;
    // 振替の必須チェックは「保存対象（差分）」だけに限定する。
    // editsByDate 全体を見ると、別日の未完了/旧データの振替に誤反応するため。
    // 新方式の振替元は closed + reschedule_from_date(=振替先) に変換済み。
    //  - status=reschedule で from 無し → 振替先未入力（不完全）
    //  - status=closed で from==元日 → 振替先が元日と同日
    const badReschedule = submitPayload.upsertOverrides.find(
      (o) =>
        (o.status === "reschedule" && !o.reschedule_from_date) ||
        (o.status === "closed" &&
          o.reschedule_from_date &&
          o.reschedule_from_date === o.date)
    );
    if (badReschedule) {
      setMessageType("error");
      const where = formatDateLabel(badReschedule.date);
      setMessage(
        badReschedule.status === "closed"
          ? `振替先には元と異なる日付を指定してください（${where}）`
          : `振替先の日付を入力してください（${where}）`
      );
      setTimeout(() => setMessage(""), 3000);
      return;
    }
    setIsSubmitting(true);
    setLoadingText("休講情報を保存中");
    setMessage("");

    // サーバ側で「振替先×全クラス休講」の衝突を見るため、取消・解除 → 追加の順に直列で送る。
    // 全校⇔校舎別の切替で置き換わる全クラス休講は、新しい方が保存できてから消す
    // （先に消すと、追加が失敗したとき休講が消えたままになる）。
    const results = [];
    const run = async (fn) => {
      try {
        await fn();
        results.push({ status: "fulfilled" });
        return true;
      } catch (reason) {
        results.push({ status: "rejected", reason });
        return false;
      }
    };
    for (const id of submitPayload.deleteScheduleIds) {
      await run(() => deleteScheduleOverride(id));
    }
    for (const d of submitPayload.deleteAcIds.filter((x) => !x.replaced)) {
      await run(() => deleteAllClosed(d.id));
    }
    const hasOverrides = submitPayload.upsertOverrides.length > 0;
    const overridesIdx = results.length;
    let overridesOk =
      !hasOverrides ||
      (await run(() => upsertScheduleOverrides(submitPayload.upsertOverrides)));
    const acUpsertOk =
      submitPayload.upsertAcItems.length === 0 ||
      (await run(() => upsertAllClosed(submitPayload.upsertAcItems)));
    let replacedDeleted = false;
    if (acUpsertOk) {
      for (const d of submitPayload.deleteAcIds.filter((x) => x.replaced)) {
        if (await run(() => deleteAllClosed(d.id))) replacedDeleted = true;
      }
    }
    // 置き換えで消えた全クラス休講の日へ振替を入れていた場合、消えた後なら通るので1回だけ再送する
    if (hasOverrides && !overridesOk && replacedDeleted) {
      // 1回目の結果は再送の結果で置き換える（件数表示を実際の操作数に合わせる）
      results.splice(overridesIdx, 1);
      overridesOk = await run(() =>
        upsertScheduleOverrides(submitPayload.upsertOverrides)
      );
    }
    const failed = results.filter((r) => r.status === "rejected");
    const succeeded = results.length - failed.length;

    if (failed.length === 0) {
      // 保存成功 → サーバから再取得して原本を最新化
      try {
        await loadScheduleOverrides(viewYear, viewMonth);
      } catch (_) {
        // 再取得失敗してもメッセージは成功（保存自体は通った）
      }
      setMessageType("success");
      setMessage("保存しました");
      setTimeout(() => setMessage(""), 3000);
    } else {
      const firstError = failed[0].reason;
      const errMsg = (firstError && firstError.message) || "保存に失敗しました";
      // 一部だけ保存できた場合は、画面の元データが古いままだと再保存が失敗し続けるため最新に更新する
      if (succeeded > 0) {
        try {
          await loadScheduleOverrides(viewYear, viewMonth);
        } catch (_) {
          // 再取得に失敗してもエラーメッセージは出す
        }
      }
      setMessageType("error");
      setMessage(
        succeeded > 0
          ? `一部だけ保存しました（失敗 ${failed.length}/${results.length} 件）: ${errMsg}　画面を最新の状態に更新したので、失敗した変更をもう一度行ってください`
          : `保存に失敗しました（${failed.length}/${results.length} 件）: ${errMsg}`
      );
      setTimeout(() => setMessage(""), 6000);
    }

    setLoadingText("");
    setIsSubmitting(false);
  };

  // 「＋ 全クラス休講 ▾」ボタンのラベルとアクティブ判定
  // - 何も設定なし: "＋ 全クラス休講"
  // - 全スタジオ休講: "全スタジオ休講中"
  // - 校舎別: "全クラス休講: N校舎"
  const allClosedButtonLabel = (() => {
    if (selectedAllClosed.length === 0) return "＋ 全クラス休講";
    if (isAllStudiosClosed) return "全スタジオ休講中";
    return `全クラス休講: ${selectedAllClosed.length}校舎`;
  })();
  const allClosedActive = selectedAllClosed.length > 0;

  // 日の編集 UI 本体（モーダル中身と SP インラインで共通使用）
  // selectedDate が null なら何も返さない（SP インラインのガード用）
  const renderDayEditContent = () => {
    if (!selectedDate) return null;
    return (
      <>
        <div className="edit-header">
          <h3>
            {selectedDate.getFullYear()}年{selectedDate.getMonth() + 1}月
            {selectedDate.getDate()}日 ({WEEKDAY_LABELS[selectedDate.getDay()]})
          </h3>
          {/* 全クラス休講ドロップダウン：「全スタジオ」or 校舎別チェック（v5 7.4.2） */}
          <div className="all-closed-dropdown-wrap">
            <button
              type="button"
              className={`all-closed-btn${allClosedActive ? " active" : ""}${allClosedDropdownOpen ? " open" : ""}`}
              onClick={() => setAllClosedDropdownOpen((v) => !v)}
              data-verify-all-closed-btn
              data-verify-all-closed-active={allClosedActive}
              aria-haspopup="menu"
              aria-expanded={allClosedDropdownOpen}
            >
              {allClosedButtonLabel} <span className="caret">▾</span>
            </button>
            {allClosedDropdownOpen && (
              <div
                className="all-closed-dropdown"
                role="menu"
                data-verify-all-closed-dropdown-open
              >
                <label className="all-closed-dropdown-item all">
                  <input
                    type="checkbox"
                    checked={isAllStudiosClosed}
                    onChange={toggleAllStudiosClosed}
                  />
                  全スタジオ休講
                </label>
                {studiosWithLessonsForSelected.map((studio) => (
                  <label
                    key={studio.id}
                    className="all-closed-dropdown-item"
                  >
                    <input
                      type="checkbox"
                      checked={isStudioChecked(studio.id)}
                      onChange={() => toggleStudioClosed(studio.id)}
                    />
                    <span
                      className="studio-badge"
                      style={{ backgroundColor: studio.color }}
                    >
                      {studio.name_ja}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 他日から振替で入るクラス（読取専用。取消は振替元の日で行う） */}
        {selectedIncoming.length > 0 && (
          <div className="incoming-reschedule-list">
            <p className="incoming-reschedule-caption">
              振替で入るクラス（取消は振替元の日で操作）
            </p>
            {selectedIncoming.map((inc, i) => {
              const studio = studios.find((s) => s.id === inc.studioId);
              return (
                <div
                  key={`${inc.originKey}-${inc.placementId}-${i}`}
                  className="incoming-reschedule-item"
                >
                  <span
                    className="studio-badge"
                    style={{ backgroundColor: studio?.color }}
                  >
                    {studio?.name_ja}
                  </span>
                  {/* 時間変更ありは「元 → 変更後」を明示（変更後が実際の開催時刻） */}
                  <span className="lesson-time">
                    {inc.timeChanged && (
                      <>
                        <span className="time-orig">
                          {inc.origStart}-{inc.origEnd}
                        </span>
                        <span className="time-arrow">→</span>
                      </>
                    )}
                    <span className={inc.timeChanged ? "time-new" : undefined}>
                      {inc.start} - {inc.end}
                    </span>
                  </span>
                  <span className="lesson-name">
                    {inc.name}
                    {inc.target ? ` ${inc.target}` : ""}
                  </span>
                  <span className="state-detail reschedule-in">
                    {formatRescheduleFrom(inc.originKey)}
                    {inc.timeChanged ? "（時間変更）" : ""}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {lessons.length === 0 && selectedIncoming.length === 0 ? (
          <div className="empty-state">
            <p>この曜日にレッスンはありません</p>
          </div>
        ) : (
          <div className="lesson-list">
            {lessons.map((lesson, idx) => {
              const studio = studios.find((s) => s.id === lesson.studioId);
              const studioClosed =
                isAllStudiosClosed || selectedAllClosed.includes(lesson.studioId);
              const isExpanded = expandedLessonId === lesson.lessonId;
              return (
                <div
                  key={lesson.lessonId}
                  className={`lesson-card status-${lesson.status} ${studioClosed ? "studio-closed" : ""}${isExpanded ? " is-expanded" : ""}`}
                  data-verify-expanded={isExpanded}
                >
                  <div
                    className="lesson-card-header"
                    onClick={() =>
                      setExpandedLessonId(isExpanded ? null : lesson.lessonId)
                    }
                  >
                    <span
                      className="studio-badge"
                      style={{ backgroundColor: studio?.color }}
                    >
                      {studio?.name_ja}
                    </span>
                    <span className="lesson-time">
                      {lesson.start} - {lesson.end}
                    </span>
                    <span className="lesson-name">
                      {lesson.name}
                      {lesson.target ? ` ${lesson.target}` : ""}
                    </span>
                    <span className="lesson-instructor">
                      {lesson.instructor}
                    </span>
                  </div>

                  {/* SP 用 折り畳み時の状態サマリー（PC では SCSS で非表示）
                      営業（open）なら何も出さない、それ以外は状態ピル + 詳細 */}
                  {lesson.status !== "open" && (
                    <div className="lesson-card-collapsed-summary">
                      {/* 振替（新方式）は元＝休講扱いなのでピルも休講で見せる */}
                      {lesson.status === "reschedule" && lesson.rescheduleTo ? (
                        <span className="state-pill state-closed">
                          {STATUS.closed.label}
                        </span>
                      ) : (
                        <span className={`state-pill state-${lesson.status}`}>
                          {STATUS[lesson.status]?.label}
                        </span>
                      )}
                      {lesson.status === "substitute" && lesson.substituteInstructor && (
                        <span className="state-detail">
                          ※ {lesson.substituteInstructor} 講師による代講
                        </span>
                      )}
                      {lesson.status === "reschedule" && lesson.rescheduleTo && (
                        <span className="state-detail">
                          → {formatRescheduleToLabel(lesson)}
                        </span>
                      )}
                      {lesson.status === "reschedule" && !lesson.rescheduleTo && lesson.rescheduleFrom && (
                        <span className="state-detail">
                          ※ {formatRescheduleFrom(lesson.rescheduleFrom)}
                        </span>
                      )}
                      {lesson.status === "timeChange" && (
                        <span className="state-detail">
                          {lesson.newStart}-{lesson.newEnd} に変更
                        </span>
                      )}
                      {lesson.status === "closed" && lesson.note && (
                        <span className="state-detail">{lesson.note}</span>
                      )}
                    </div>
                  )}

                  <div className="status-tabs">
                    {Object.entries(STATUS).map(([key, s]) => (
                      <button
                        key={key}
                        type="button"
                        className={`status-tab ${lesson.status === key ? "on" : ""}`}
                        onClick={() => updateLesson(idx, { status: key })}
                        disabled={studioClosed}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>

                  {lesson.status === "substitute" && (
                    <div className="lesson-detail">
                      <label>代講講師名</label>
                      <input
                        type="text"
                        placeholder="例:AKARI"
                        value={lesson.substituteInstructor}
                        onChange={(e) =>
                          updateLesson(idx, {
                            substituteInstructor: e.target.value,
                          })
                        }
                      />
                    </div>
                  )}

                  {lesson.status === "reschedule" && (
                    <div className="lesson-detail time-change">
                      <div className="time-change-row">
                        <div className="field">
                          <label>振替先の日付</label>
                          <input
                            type="date"
                            value={lesson.rescheduleTo}
                            onChange={(e) =>
                              updateLesson(idx, {
                                rescheduleTo: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="field">
                          {/* 時刻が変わる振替用。既定は元クラスの時刻で、変えた時だけ反映される */}
                          <label>振替先の時間</label>
                          <div className="time-inputs">
                            <input
                              type="time"
                              value={lesson.newStart}
                              onChange={(e) =>
                                updateLesson(idx, { newStart: e.target.value })
                              }
                            />
                            <span>〜</span>
                            <input
                              type="time"
                              value={lesson.newEnd}
                              onChange={(e) =>
                                updateLesson(idx, { newEnd: e.target.value })
                              }
                            />
                          </div>
                        </div>
                      </div>
                      {lesson.rescheduleTo && (
                        <p className="reschedule-preview">
                          {formatDateLabel(selectedKey)}は休講、
                          {formatDateLabel(lesson.rescheduleTo)}
                          {isRescheduleTimeChanged(lesson)
                            ? ` ${lesson.newStart}-${lesson.newEnd}`
                            : ""}
                          に「{formatRescheduleFrom(selectedKey)}」と表示されます
                        </p>
                      )}
                    </div>
                  )}

                  {lesson.status === "timeChange" && (
                    <div className="lesson-detail time-change">
                      <div className="time-change-row">
                        <div className="field">
                          <label>変更後の時間</label>
                          <div className="time-inputs">
                            <input
                              type="time"
                              value={lesson.newStart}
                              onChange={(e) =>
                                updateLesson(idx, { newStart: e.target.value })
                              }
                            />
                            <span>〜</span>
                            <input
                              type="time"
                              value={lesson.newEnd}
                              onChange={(e) =>
                                updateLesson(idx, { newEnd: e.target.value })
                              }
                            />
                          </div>
                        </div>
                        <div className="field grow">
                          <label>
                            振替割振の有無
                            <span className="note-count">
                              (文字数 {lesson.note.length}/{NOTE_MAX})
                            </span>
                          </label>
                          <input
                            type="text"
                            placeholder="例：3/7日分の時間を割らせていただきます"
                            maxLength={NOTE_MAX}
                            value={lesson.note}
                            onChange={(e) =>
                              updateLesson(idx, { note: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {lesson.status === "closed" && (
                    <div className="lesson-detail">
                      <label>
                        備考（任意）
                        <span className="note-count">
                          (文字数 {lesson.note.length}/{NOTE_MAX})
                        </span>
                      </label>
                      <input
                        type="text"
                        placeholder="任意のメモ（画像にも表示されます）"
                        maxLength={NOTE_MAX}
                        value={lesson.note}
                        onChange={(e) =>
                          updateLesson(idx, { note: e.target.value })
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <div className={`editarea schedule-mgmt${selectedDate ? " editing" : ""}`}>
        <Breadcrumbs breadcrumbs={BREADCRUMBS} />
        <StatusMessage message={message} type={messageType} />

        <div className="form">
          {/* 画像出力（Phase 7-G）: 右上にアイコンボタン。
              プレビュー（スマホ=長押し保存 / PC=確認）は常時表示。
              ダウンロード（zip 一括）は PC のみ表示（CSS の @media で SP 非表示）。
              生成中は画面に loadingText オーバーレイが出る。 */}
          <div className="image-export-wrap">
            <button
              type="button"
              className="image-export-btn icon-only"
              disabled={previewLoading || studios.length === 0}
              onClick={handlePreview}
              aria-label="スケジュール画像をプレビュー"
              title="プレビュー"
            >
              <FontAwesomeIcon icon={faImages} />
            </button>
            <button
              type="button"
              className="image-export-btn icon-only download-only"
              disabled={previewLoading || studios.length === 0}
              onClick={handleDownload}
              aria-label="スケジュール画像をダウンロード"
              title="ダウンロード（zip）"
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
          </div>

          {/* 月切替ヘッダー */}
          <div className="buttonWrap">
            <button type="button" onClick={handlePrevMonth}>
              ‹ 前月
            </button>
            <span className="month-label" translate="no">
              {viewYear}年 {viewMonth + 1}月
            </span>
            <button type="button" onClick={handleNextMonth}>
              翌月 ›
            </button>
          </div>

          {/* オフスクリーン描画（画像化対象、画面外に配置。全校舎分を同時に描画） */}
          {offscreen && (
            <div
              style={{
                position: "fixed",
                left: -99999,
                top: 0,
                pointerEvents: "none",
              }}
              aria-hidden="true"
            >
              {offscreen.items.map((it) => (
                <div
                  key={it.studioId}
                  ref={(el) => {
                    imageNodeRefs.current[it.studioId] = el;
                  }}
                >
                  <ScheduleImageTemplate
                    year={offscreen.year}
                    month={offscreen.month}
                    studioId={it.studioId}
                    entries={it.entries}
                    onFit={(info) => {
                      imageOverflowRef.current[it.studioId] = info.overflow;
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="schedule-grid">
            {/* カレンダー */}
            <section className="calendar-card">
              <div className="calendar-grid">
                {WEEKDAY_LABELS.map((wd, i) => (
                  <div
                    key={wd}
                    className={`weekday-cell ${i === 0 ? "sun" : ""} ${i === 6 ? "sat" : ""}`}
                  >
                    {wd}
                  </div>
                ))}
                {matrix.map((date, i) => {
                  if (!date) return <div key={i} className="day-cell empty" />;
                  const key = dateKey(date);
                  const isSelected = selectedKey === key;
                  const isToday = dateKey(today) === key;
                  const summaries = getStudioSummaries(date);
                  const dow = date.getDay();
                  // スクリーンリーダー向け：日付＋集計バッジを 1 つの aria-label に集約
                  const ariaLabel =
                    `${date.getMonth() + 1}月${date.getDate()}日（${WEEKDAY_LABELS[dow]}）` +
                    (summaries.length > 0
                      ? " " + summaries.map((s) => s.label).join(" ")
                      : "");
                  return (
                    <button
                      type="button"
                      key={i}
                      aria-label={ariaLabel}
                      className={`day-cell ${isSelected ? "selected" : ""} ${isToday ? "today" : ""} ${dow === 0 ? "sun" : ""} ${dow === 6 ? "sat" : ""}`}
                      onClick={() => handleSelectDate(date)}
                    >
                      <span className="day-number" aria-hidden="true">{date.getDate()}</span>
                      {summaries.length > 0 && (
                        <span className="day-summary-stack">
                          {summaries.map((s, idx) => {
                            // 全クラス休講: 中央寄せの集約バッジ
                            if (s.kind === "all") {
                              return (
                                <span
                                  key={`all-${idx}`}
                                  className="day-summary-allclosed"
                                  data-verify-summary-kind="all"
                                >
                                  全クラス休講
                                </span>
                              );
                            }
                            // 校舎別 1 行表示。左カラーバーは statuses の色を使う
                            // （複数状態なら 2 色グラデで上下分割、C-4 デザイン）
                            // SP では SCSS が --state-color-1/2 を背景に使う
                            const c1 = s.statuses[0].color;
                            const c2 = s.statuses[1]?.color ?? c1;
                            const barStyle =
                              s.statuses.length >= 2
                                ? {
                                    borderImage: `linear-gradient(180deg, ${c1} 50%, ${c2} 50%) 1`,
                                    "--state-color-1": c1,
                                    "--state-color-2": c2,
                                  }
                                : {
                                    borderLeftColor: c1,
                                    "--state-color-1": c1,
                                    "--state-color-2": c1,
                                  };
                            return (
                              <span
                                key={`studio-${s.studioId}-${idx}`}
                                className="day-summary-line"
                                style={barStyle}
                                data-verify-summary-kind="studio"
                                data-verify-studio-id={s.studioId}
                                data-verify-is-full-all={s.isFullAll}
                              >
                                <span className="day-summary-studio">{s.studioName}</span>
                                <span className="day-summary-state">
                                  {s.prefix}{s.statusLabelText}
                                </span>
                              </span>
                            );
                          })}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Phase 7-G-pre: 新セル UI でカラーバーが状態を明示するため、凡例は廃止 */}
            </section>

            {/* Phase 7-G-pre: SP 用インライン編集 UI（PC では SCSS で非表示）
                カレンダー直下にその日の編集 UI を展開 */}
            {selectedDate && (
              <section
                className={`day-edit-inline${allClosedDropdownOpen ? " dropdown-open" : ""}`}
                aria-label="休講編集（SP インライン）"
                data-verify-sp-edit
              >
                {renderDayEditContent()}
              </section>
            )}

          </div>
        </div>

        <div className="submit-area">
          <button
            type="reset"
            onClick={handleReset}
            disabled={!isDirty || isSubmitting}
          >
            Reset
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={!isDirty || isSubmitting}
          >
            Submit
          </button>
        </div>
      </div>

      {/* 編集モーダル（.editarea の backdrop-filter による containing block 問題を避けるためフラグメント直下） */}
      {selectedDate && (
        <div
          className="edit-modal-overlay"
          role="presentation"
          onClick={handleCloseModal}
        >
          <section
            className="edit-modal"
            role="dialog"
            aria-modal="true"
            aria-label="休講編集"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="modal-close"
              onClick={handleCloseModal}
              aria-label="閉じる"
            >
              ×
            </button>
            {renderDayEditContent()}
          </section>
        </div>
      )}

      {/* 投稿用画像プレビュー（Phase 7-G）: 全校舎の 1080×1920 を縮小表示して確認 */}
      {preview && (
        <div
          className="preview-modal-overlay"
          role="presentation"
          onClick={closePreview}
        >
          <section
            className="preview-modal"
            role="dialog"
            aria-modal="true"
            aria-label="投稿用画像プレビュー"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="modal-close"
              onClick={closePreview}
              aria-label="閉じる"
            >
              ×
            </button>
            <h3 className="preview-title">
              Preview（{preview.year}/{preview.month}）
            </h3>
            <div className="preview-grid">
              {preview.images.map((img) => (
                <figure key={img.studioId} className="preview-card">
                  <figcaption className="preview-caption">
                    {img.studioName}
                  </figcaption>
                  <img
                    className="preview-image"
                    src={img.dataUrl}
                    alt={`${img.studioName} ${preview.year}年${preview.month}月のスケジュール`}
                    width={1080 * PREVIEW_SCALE}
                    height={1920 * PREVIEW_SCALE}
                  />
                </figure>
              ))}
            </div>
          </section>
        </div>
      )}

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}
    </>
  );
}

export default SchedulePage;
